class SamagoHomeHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-carousel-animated-slide]'));
    this.heroSlide = this.querySelector('.samago-home-hero-carousel__slide--hero');
    this.video = this.querySelector('.samago-home-hero-carousel__slide--video video');
    this.videoSlide = this.querySelector('.samago-home-hero-carousel__slide--video');
    this.previousButtons = Array.from(this.querySelectorAll('[data-carousel-previous]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-carousel-next]'));
    this.desktopMedia = window.matchMedia('(min-width: 750px)');
    this.giftAbort?.abort();
    this.giftAbort = new AbortController();
    this.querySelectorAll('[data-mothers-day-gift]').forEach(gift => {
      const trigger = gift.querySelector('button');
      const coupon = gift.querySelector('.samago-mothers-day-gift__coupon');
      const reveal = () => {
        if (this.isTransitioning) return;
        const expiresAt = Date.parse(gift.dataset.expiresAt);
        if (Date.now() >= expiresAt) {
          gift.hidden = true;
          coupon.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          return;
        }
        gift.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        coupon.hidden = false;
        this.stop();
      };
      const options = { signal: this.giftAbort.signal };
      trigger.addEventListener('click', reveal, options);
      trigger.addEventListener('focus', reveal, options);
      gift.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') reveal();
      }, options);
    });
    this.currentSlideIndex = -1;
    this.isTransitioning = false;
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.onVideoClick = this.handleVideoClick.bind(this);
    this.onHeroClick = this.handleHeroClick.bind(this);
    this.onMobileVideoReady = this.ensureMobileAutoplay.bind(this);
    this.onPreviousClick = () => this.navigate(-1);
    this.onNextClick = () => this.navigate(1);
    this.desktopMedia.addEventListener('change', this.onViewportChange);
    this.video?.addEventListener('click', this.onVideoClick);
    this.heroSlide?.addEventListener('click', this.onHeroClick);
    this.video?.addEventListener('loadeddata', this.onMobileVideoReady);
    this.video?.addEventListener('canplay', this.onMobileVideoReady);
    this.previousButtons.forEach(button => button.addEventListener('click', this.onPreviousClick));
    this.nextButtons.forEach(button => button.addEventListener('click', this.onNextClick));
    this.handleViewportChange();
  }

  disconnectedCallback() {
    this.giftAbort?.abort();
    this.desktopMedia?.removeEventListener('change', this.onViewportChange);
    this.video?.removeEventListener('click', this.onVideoClick);
    this.heroSlide?.removeEventListener('click', this.onHeroClick);
    this.video?.removeEventListener('loadeddata', this.onMobileVideoReady);
    this.video?.removeEventListener('canplay', this.onMobileVideoReady);
    this.previousButtons.forEach(button => button.removeEventListener('click', this.onPreviousClick));
    this.nextButtons.forEach(button => button.removeEventListener('click', this.onNextClick));
    this.stop();
  }

  handleViewportChange() {
    if (this.desktopMedia.matches) {
      this.startDesktop();
      return;
    }

    this.startMobile();
  }

  ensureMobileAutoplay() {
    if (this.desktopMedia.matches || !this.video) return;
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.preload = 'auto';
    this.video.play().catch(() => {});
  }

  startDesktop() {
    if (this.video) {
      this.video.currentTime = 0;
      this.video.pause();
    }
    this.setSlideVisibility(this.videoSlide, false);
    this.showFirstSlide();
  }

  startMobile() {
    this.showFirstSlide();
    this.setSlideVisibility(this.videoSlide, true);
    this.ensureMobileAutoplay();
  }

  showFirstSlide() {
    this.stop();
    this.resetSlides();
    this.setTransitioning(false);
    this.currentSlideIndex = 0;
    const firstSlide = this.slides[0];
    if (!firstSlide) return;
    this.setSlideVisibility(firstSlide, true);
    firstSlide.classList.add('is-visible');
    this.timer = window.setTimeout(() => this.advanceAfterVisiblePeriod(), 7000);
  }

  resetSlides() {
    [...this.slides, this.videoSlide].filter(Boolean).forEach((slide) => {
      slide.classList.remove('is-entering', 'is-visible', 'is-exiting', 'is-resetting');
      this.setSlideVisibility(slide, false);
    });
  }

  setTransitioning(value) {
    this.isTransitioning = value;
    [...this.previousButtons, ...this.nextButtons].forEach(button => button.setAttribute('aria-disabled', String(value)));
  }

  setSlideVisibility(slide, visible) {
    if (!slide) return;
    slide.setAttribute('aria-hidden', String(!visible));
    slide.inert = !visible;
    this.syncSlideVideos(slide, visible);
  }

  syncSlideVideos(slide, visible) {
    slide.querySelectorAll?.('[data-carousel-slide-video]').forEach(video => {
      const forDesktop = video.getAttribute('data-carousel-slide-video') === 'desktop';
      if (!visible || forDesktop !== this.desktopMedia.matches) {
        video.pause();
        return;
      }
      if (!video.paused) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.currentTime = 0;
      video.play().catch(() => {});
    });
  }

  rememberNavigationFocus(slide) {
    const focused = slide?.querySelector?.('[data-carousel-previous]:focus, [data-carousel-next]:focus');
    this.navigationFocus = focused ? (focused.hasAttribute('data-carousel-previous') ? '[data-carousel-previous]' : '[data-carousel-next]') : null;
  }

  restoreNavigationFocus(slide) {
    if (!this.navigationFocus) return;
    slide.querySelector(this.navigationFocus)?.focus({ preventScroll: true });
    this.navigationFocus = null;
  }

  prepareDirection(slide, direction) {
    this.setAttribute('data-carousel-direction', direction < 0 ? 'previous' : 'next');
    slide.classList.remove('is-exiting', 'is-resetting');
    void slide.offsetWidth;
  }

  navigate(direction) {
    if (this.isTransitioning || !this.slides.length) return;
    this.stop();
    if (this.desktopMedia.matches) {
      if (this.currentSlideIndex === -1) {
        this.enterDesktopSlides(direction < 0 ? this.slides.length - 1 : 0, direction);
        return;
      }
      const nextIndex = this.currentSlideIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.slides.length) {
        this.exitToVideo(direction);
        return;
      }
      this.transitionToSlide(nextIndex, direction);
      return;
    }
    const nextIndex = (this.currentSlideIndex + direction + this.slides.length) % this.slides.length;
    this.transitionToSlide(nextIndex, direction);
  }

  enterDesktopSlides(index = 0, direction = 1) {
    this.transitionToSlide(index, direction);
  }

  transitionToSlide(nextIndex, direction = 1) {
    const currentSlide = this.currentSlideIndex === -1 ? this.videoSlide : this.slides[this.currentSlideIndex];
    const nextSlide = nextIndex === -1 ? this.videoSlide : this.slides[nextIndex];
    if (!currentSlide || !nextSlide || currentSlide === nextSlide || this.isTransitioning) return;

    this.rememberNavigationFocus(currentSlide);
    this.setTransitioning(true);
    this.prepareDirection(nextSlide, direction);
    if (nextSlide === this.videoSlide && this.video) {
      this.video.pause();
      this.video.currentTime = 0;
      this.video.play().catch(() => {});
    }
    currentSlide.classList.remove('is-visible');
    currentSlide.classList.add('is-exiting');
    this.setSlideVisibility(nextSlide, true);
    nextSlide.classList.add('is-entering');

    this.timer = window.setTimeout(() => {
      this.setSlideVisibility(currentSlide, false);
      if (currentSlide === this.videoSlide) this.video?.pause();
      currentSlide.classList.remove('is-exiting');
      currentSlide.classList.add('is-resetting');
      nextSlide.classList.replace('is-entering', 'is-visible');
      this.currentSlideIndex = nextIndex;
      this.setTransitioning(false);
      this.restoreNavigationFocus(nextSlide);
      this.resetFrame = window.requestAnimationFrame(() => {
        currentSlide.classList.remove('is-resetting');
      });
      this.timer = window.setTimeout(() => this.advanceAfterVisiblePeriod(), nextIndex === -1 ? 10000 : 7000);
    }, 700);
  }

  advanceAfterVisiblePeriod() {
    if (this.desktopMedia.matches && this.currentSlideIndex === this.slides.length - 1) {
      this.exitToVideo();
      return;
    }

    const nextIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.transitionToSlide(nextIndex);
  }

  exitToVideo(direction = 1) {
    this.transitionToSlide(-1, direction);
  }

  handleHeroClick(event) {
    const interactiveSelector = 'a, button, input, select, textarea, [role="button"]';
    if (event.target.closest?.(interactiveSelector)) return;
    if (this.slides[this.currentSlideIndex] !== this.heroSlide || this.isTransitioning) return;

    this.navigate(1);
  }

  handleVideoClick() {
    if (!this.desktopMedia.matches || this.currentSlideIndex !== -1) return;
    this.navigate(1);
  }

  stop() {
    window.clearTimeout(this.timer);
    window.cancelAnimationFrame(this.resetFrame);
  }
}

if (!customElements.get('samago-home-hero-carousel')) {
  customElements.define('samago-home-hero-carousel', SamagoHomeHeroCarousel);
}
